import React, { useState, useEffect, useCallback, useRef } from 'react'
import { Prompt, useLocation, useHistory } from 'react-router-dom'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Box from '@mui/material/Box'
import FormControl from '@mui/material/FormControl'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import IconButton from '@mui/material/IconButton'
import InputBase from '@mui/material/InputBase'
import SearchIcon from '@mui/icons-material/Search'
import Button from '@mui/material/Button'
import Add from '@mui/icons-material/Add'

import Title from '../../component/Title'
import { LIST_FILTER_SEARCH_FIELD_SX, LIST_FILTER_SEARCH_BUTTON_SX, ListFilterBar, ListFilterForm } from '../../component/ListFilterLayout'
import API from '../../component/Axios'
import Snackbars from '../../component/Snackbars'
import BottomButtonGroup, { FORM_COMPACT_ACTION_BUTTON_SX, FORM_WIDE_UPLOAD_BUTTON_SX } from '../../component/BottomButtonGroup'
import Pagination from '../../component/Pagination'
import { FORM_IMAGE_PREVIEW_FRAME_SX, FORM_PANEL_SX } from '../../component/formSurfaceStyles'
import { getLocationState } from '../../utils/listPageState'
import {
    applyAppCoupangBannerDraft,
    applyAppCoupangBannerFieldChange,
    APP_COUPANG_BANNER_IMAGE_FIELD_CONFIGS,
    APP_COUPANG_BANNER_IMAGE_FILE_ACCEPT,
    APP_COUPANG_BANNER_IMAGE_FILE_ERROR_MESSAGE,
    clearAppCoupangBannerDraft,
    createAppCoupangBannerDraftPayload,
    createDefaultAppCoupangBannerParams,
    handleAppCoupangBannerBackNavigation,
    handleAppCoupangBannerImageUpload,
    moveToAppCoupangBannerList,
    NEW_APP_COUPANG_BANNER_DRAFT_KEY,
    prepareAppCoupangBannerSubmit,
    readAppCoupangBannerDraft,
    saveAppCoupangBannerDraft,
} from './appCoupangBannerDraftUtils'
import {
    APP_COUPANG_BANNER_IMAGE_LEAVE_MESSAGE,
    hasPendingAppCoupangBannerImage,
    usePendingAppCoupangBannerImageUnloadGuard,
} from './appCoupangBannerLeaveGuard'

const PANEL_SX = FORM_PANEL_SX

const IMAGE_PREVIEW_FRAME_SX = {
    ...FORM_IMAGE_PREVIEW_FRAME_SX,
    width: '100%',
    height: { xs: 220, md: 280 },
    mb: 1.5,
}

const IMAGE_PREVIEW_SX = {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    display: 'block',
}

const getImageFileName = (imageFile) => {
    if (!imageFile || typeof imageFile !== 'object') {
        return ''
    }

    return imageFile.name || ''
}

function FormSection({ title, description, children }) {
    return (
        <Paper variant="outlined" sx={PANEL_SX}>
            <Box sx={{ mb: 1.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    {title}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    {description}
                </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {children}
            </Box>
        </Paper>
    )
}

function CoupangBannerImageUploadPanel({
    fieldConfig,
    imageSrc,
    imageFile,
    disabled,
    onUploadClick,
}) {
    const imageFileName = getImageFileName(imageFile)

    return (
        <Paper variant="outlined" sx={PANEL_SX}>
            <Box sx={{ mb: 1.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    {fieldConfig.previewAlt}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    App 최저가 배너 화면에 노출되는 이미지를 등록합니다.
                </Typography>
            </Box>
            <Box sx={IMAGE_PREVIEW_FRAME_SX}>
                {imageSrc ? (
                    <Box
                        component="img"
                        src={imageSrc}
                        alt={`${fieldConfig.previewAlt} 미리보기`}
                        sx={IMAGE_PREVIEW_SX}
                    />
                ) : (
                    <Typography variant="body2" color="text.secondary">
                        이미지를 선택하면 미리보기가 표시됩니다.
                    </Typography>
                )}
            </Box>
            <FormControl fullWidth>
                <TextField
                    inputProps={{ accept: APP_COUPANG_BANNER_IMAGE_FILE_ACCEPT }}
                    style={{ display: 'none' }}
                    name={fieldConfig.fieldName}
                    id={fieldConfig.inputId}
                    type="file"
                    disabled={disabled}
                    onChange={onUploadClick}
                />
                <Box component="label" htmlFor={fieldConfig.inputId} sx={{ display: 'block' }}>
                    <Button
                        fullWidth
                        variant="contained"
                        color="success"
                        component="span"
                        startIcon={<Add />}
                        sx={FORM_WIDE_UPLOAD_BUTTON_SX}
                        disabled={disabled}
                    >
                        {fieldConfig.uploadLabel}
                    </Button>
                </Box>
            </FormControl>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                {imageFileName ? `선택한 파일: ${imageFileName}` : '저장 전 미리보기로 이미지가 올바른지 확인하세요.'}
            </Typography>
        </Paper>
    )
}

export default function NewAppCoupangBanner() {
    const location = useLocation()
    const history = useHistory()
    const locationState = getLocationState(location)
    const listCurPage = locationState.curPage || 1
    const listSearchText = locationState.searchText
    const listSearchSelect = locationState.searchSelect
    const listSearch = location.search || ''
    const draftKey = NEW_APP_COUPANG_BANNER_DRAFT_KEY
    const storedDraftRef = useRef(readAppCoupangBannerDraft(draftKey))
    const [saving, setSaving] = useState(false)
    const [totalCount, setTotalCount] = useState(0)
    const [curPage, setCurPage] = useState(() => {
        const storedDraft = storedDraftRef.current
        return storedDraft?.pickerPage ? Number(storedDraft.pickerPage) || 1 : 1
    })
    const offset = 5
    const [datas, setDatas] = useState([])
    const [params, setParams] = useState(() => createDefaultAppCoupangBannerParams())
    const [image, setImage] = useState('')
    const [openSnackbar, setOpenSnackbar] = useState(false)
    const [severitySnackbar, setSeveritySnackbar] = useState('info')
    const [messageSnackbar, setMessageSnackbar] = useState('')
    const [searchProductName, setSearchProductName] = useState(() => storedDraftRef.current?.pickerSearchText || '')
    const searchProductNameRef = useRef(storedDraftRef.current?.pickerSearchText || '')
    const [productSearchSelect] = useState({
        keyword1: '',
    })
    const [draftReady, setDraftReady] = useState(false)
    const [navigationUnlocked, setNavigationUnlocked] = useState(false)
    const initialDraftSnapshotRef = useRef('')
    const leaveGuardEnabled = hasPendingAppCoupangBannerImage(params) && !saving && !navigationUnlocked
    const imageFieldConfigs = APP_COUPANG_BANNER_IMAGE_FIELD_CONFIGS

    usePendingAppCoupangBannerImageUnloadGuard(leaveGuardEnabled)

    useEffect(() => {
        const baseParams = createDefaultAppCoupangBannerParams()
        const restoredDraft = applyAppCoupangBannerDraft({
            baseParams,
            basePickerPage: 1,
            basePickerSearchText: '',
            draft: storedDraftRef.current,
        })

        initialDraftSnapshotRef.current = JSON.stringify(createAppCoupangBannerDraftPayload({
            params: baseParams,
            pickerPage: 1,
            pickerSearchText: '',
        }))

        setParams((prev) => ({
            ...prev,
            ...restoredDraft.params,
        }))
        setCurPage(restoredDraft.pickerPage)
        searchProductNameRef.current = restoredDraft.pickerSearchText
        setSearchProductName(restoredDraft.pickerSearchText)
        setDraftReady(true)
    }, [])

    const getData = useCallback(async () => {
        try {
            const { data } = await API.post('/api/v1/admin/appCoupangBanner/getCoupangList', {
                page: curPage,
                offset: offset,
                searchText: searchProductNameRef.current,
                searchSelect: productSearchSelect
            })
            if (data.code === 200) {
                setTotalCount(data.data.count)
                setDatas(data.data.rows)
            } else if (data.code === 403) {
                history.push('/login')
                return
            } else {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(data.message)
            }
        } catch (_error) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('쿠팡 상품 목록을 불러오지 못했습니다.')
        }
    }, [curPage, history, productSearchSelect])

    useEffect(() => {
        getData()
    }, [getData])

    useEffect(() => {
        if (!draftReady) {
            return
        }

        // 최저가 배너 등록은 선택 상품과 쿠팡 상품 검색 상태를 sessionStorage에 함께 보존한다.
        const nextDraft = createAppCoupangBannerDraftPayload({
            params,
            pickerPage: curPage,
            pickerSearchText: searchProductName,
        })
        const nextDraftSnapshot = JSON.stringify(nextDraft)

        if (nextDraftSnapshot === initialDraftSnapshotRef.current) {
            clearAppCoupangBannerDraft(draftKey)
            return
        }

        saveAppCoupangBannerDraft(draftKey, nextDraft)
    }, [curPage, draftKey, draftReady, params, searchProductName])

    const handleClose = () => {
        setOpenSnackbar(false)
    }

    const moveToList = (snackbarState) => {
        moveToAppCoupangBannerList({
            draftKey,
            clearDraft: clearAppCoupangBannerDraft,
            setNavigationUnlocked,
            history,
            listSearch,
            snackbarState,
            curPage: listCurPage,
            searchText: listSearchText,
            searchSelect: listSearchSelect,
        })
    }

    const handleBackClick = (event) => {
        handleAppCoupangBannerBackNavigation({
            event,
            leaveGuardEnabled,
            draftKey,
            clearDraft: clearAppCoupangBannerDraft,
            confirmLeave: () => window.confirm(APP_COUPANG_BANNER_IMAGE_LEAVE_MESSAGE),
            moveToList,
        })
    }

    const handleUpdateField = (e) => {
        setParams((prev) => applyAppCoupangBannerFieldChange({
            params: prev,
            name: e.target.name,
            value: e.target.value,
        }))
    }

    const handleUploadClick = (e) => {
        handleAppCoupangBannerImageUpload({
            event: e,
            setParams,
            setImage,
            onInvalidFile: (message = APP_COUPANG_BANNER_IMAGE_FILE_ERROR_MESSAGE) => {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(message)
            },
        })
    }

    const createData = async (formData) => {
        try {
            setSaving(true)

            const { data } = await API({
                method: 'post',
                url: '/api/v1/admin/appCoupangBanner/create',
                data: formData,
                headers: { "Content-Type": "multipart/form-data" }
            })

            if (data.code === 200) {
                // 등록 후에도 이전 최저가 배너 목록 조건으로 그대로 복귀시킨다.
                moveToList({
                    severity: 'success',
                    message: data.message
                })
            } else if (data.code === 403) {
                history.push('/login')
            } else {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(data.message)
            }
        } catch (_error) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('App 최저가 배너를 등록하지 못했습니다.')
        } finally {
            setSaving(false)
        }
    }

    const handleSubmit = (e) => {
        e.preventDefault()

        if (saving) {
            return
        }

        const { nextParams, errorMessage, formData } = prepareAppCoupangBannerSubmit({
            params,
            requireImage: true,
        })

        setParams(nextParams)

        if (errorMessage) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar(errorMessage)
            return
        }

        createData(formData)
    }

    const searchButtonClick = () => {
        if (curPage === 1) {
            getData()
            return
        }

        setCurPage(1)
    }

    const chooseButtonClick = (coupangId, productName) => {
        setParams((prev) => ({
            ...prev,
            coupangId: coupangId, 
            productName: productName,
        }))
    }
    
    return (
        <Grid item xs={12}>
            <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                <Title>APP 최저가 배너 등록</Title>
                <Prompt when={leaveGuardEnabled} message={APP_COUPANG_BANNER_IMAGE_LEAVE_MESSAGE} />
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', opacity: saving ? 0.5 : 1, pointerEvents: saving ? 'none' : 'auto' }}>
                    {/* 검색폼 */}
                    <ListFilterBar>
                        <ListFilterForm onSubmit={(e) => {
                            e.preventDefault()
                            searchButtonClick()
                        }}>
                            <InputBase
                                sx={LIST_FILTER_SEARCH_FIELD_SX}
                                placeholder="상품명"
                                type="search"
                                name="searchProductName"
                                value={searchProductName}
                                disabled={saving}
                                onChange={(e) => {
                                    searchProductNameRef.current = e.target.value
                                    setSearchProductName(e.target.value)
                                }}
                            />
                            <IconButton type="submit" sx={LIST_FILTER_SEARCH_BUTTON_SX} aria-label="search" disabled={saving}><SearchIcon /></IconButton>
                        </ListFilterForm>
                    </ListFilterBar>
                    
                    {/* 테이블 */}
                    
                    <Table size="small">
                        <caption>Total Count: {totalCount}</caption>
                        <TableHead>
                            <TableRow>
                                <TableCell>No</TableCell>
                                <TableCell>상품이미지</TableCell>
                                <TableCell>카테고리</TableCell>
                                <TableCell>하위카테고리</TableCell>
                                <TableCell>상품명</TableCell>
                                <TableCell align="center">url</TableCell>
                                <TableCell align="right">Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {datas.map((row) => (
                            <TableRow key={row.coupangId} sx={{ backgroundColor: row.coupangId === params.coupangId ? 'silver' : '' }}>
                                <TableCell>{row.rownum}</TableCell>
                                <TableCell><img className="listImg" src={row.productImage} alt="" /></TableCell>
                                <TableCell>{row.keyword1}</TableCell>
                                <TableCell>{row.keyword2}</TableCell>
                                <TableCell>{row.productName}</TableCell>
                                <TableCell>
                                    <Button
                                        onClick={() => {
                                            window.open(row.landingUrl ,'_blank', 'noopener noreferrer')
                                        }}
                                        disabled={saving}
                                    >
                                        Link
                                    </Button>
                                </TableCell>
                                <TableCell align="right">
                                    <Button
                                        variant="contained"
                                        color="success"
                                        size="small"
                                        sx={FORM_COMPACT_ACTION_BUTTON_SX}
                                        onClick={() => chooseButtonClick(row.coupangId, row.productName)}
                                        disabled={saving}
                                    >
                                        선택
                                    </Button>
                                </TableCell>
                            </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    {totalCount > 0 && <Pagination curPage={curPage} offset={offset} totalCount={totalCount} setCurPage={setCurPage} />}
                </Paper>
                <Box component="form" encType="multipart/form-data" onSubmit={handleSubmit} sx={{ opacity: saving ? 0.6 : 1 }}>
                    <Grid container spacing={2} sx={{ marginTop: 2 }}>
                        {imageFieldConfigs.map((fieldConfig) => (
                            <Grid key={fieldConfig.inputId} item xs={12} md={5}>
                                <CoupangBannerImageUploadPanel
                                    fieldConfig={fieldConfig}
                                    imageSrc={image}
                                    imageFile={params[fieldConfig.fieldName]}
                                    disabled={saving}
                                    onUploadClick={handleUploadClick}
                                />
                            </Grid>
                        ))}
                        <Grid item xs={12} md={7}>
                            <FormSection
                                title="배너 정보"
                                description="선택한 쿠팡 상품과 배너 노출 순서를 확인합니다."
                            >
                                <FormControl fullWidth>
                                    <TextField
                                        label="상품명"
                                        required
                                        id="productName"
                                        name="productName"
                                        multiline
                                        rows={2}
                                        value={params.productName || ''}
                                        size="small"
                                        disabled={saving}
                                        InputProps={{ readOnly: true }}
                                    />
                                </FormControl>
                                <Box>
                                    <FormControl sx={{ width: 120 }}>
                                        <TextField
                                            type="number"
                                            InputProps={{ inputProps: { min: 0, max: 99 } }}
                                            label="노출순서"
                                            id="order"
                                            name="order"
                                            value={params.order}
                                            disabled={saving}
                                            onChange={handleUpdateField}
                                            size="small"
                                        />
                                    </FormControl>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                        숫자가 작을수록 먼저 노출됩니다. 입력 범위는 0~99입니다.
                                    </Typography>
                                </Box>
                            </FormSection>
                        </Grid>
                    </Grid>
                    <BottomButtonGroup
                        path="/appCoupangBanners"
                        search={listSearch}
                        visible={true}
                        curPage={listCurPage}
                        searchText={listSearchText}
                        searchSelect={listSearchSelect}
                        submitDisabled={saving}
                        submitLabel={saving ? '저장 중...' : '저장'}
                        onBackClick={handleBackClick}
                    />
                </Box>
                {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
            </Paper>
        </Grid>
    )
}
